// src/components/RecentCallsList.tsx

import React from "react";
import {
  PhoneCall,
  ClipboardList,
  Calendar as CalendarIcon,
  Zap,
  ZapOff,
  Clock3,
} from "lucide-react";
import type { SummaryCall } from "../types";
import { format } from "date-fns";

interface Props {
  calls: SummaryCall[];
  selectedCallId?: string;
  onSelect: (call: SummaryCall) => void;
  loading?: boolean;
}

export default function RecentCallsList({
  calls,
  selectedCallId,
  onSelect,
  loading,
}: Props) {
  if (loading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-md text-center text-gray-500">
        Loading recent calls...
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-md text-center text-gray-500">
        No recent calls found
      </div>
    );
  }

  // sort descending by received_at
  const sorted = [...calls].sort(
    (a, b) =>
      new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
  );

  // group by date string "yyyy-MM-dd"
  const grouped: Record<string, SummaryCall[]> = {};
  sorted.forEach((call) => {
    const day = format(new Date(call.received_at), "yyyy-MM-dd");
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(call);
  });

  const renderSection = (date: string, items: SummaryCall[]) => (
    <div key={date} className="mb-4">
      <div className="text-gray-500 text-sm font-semibold mb-2">{date}</div>
      <ul className="space-y-4">
        {items.map((call) => {
          const isSelected = call.job_id === selectedCallId;

          // choose icon & color based on call type
          const isAdHoc = call.metadata.type.toLowerCase().includes("ad-hoc");
          const Icon = isAdHoc ? ClipboardList : PhoneCall;
          const iconColor = isAdHoc ? "text-red-600" : "text-blue-600";

          // format call duration
          const totalSecs = Math.floor(call.call_duration_seconds);
          const durationText =
            totalSecs < 60
              ? `${totalSecs}s`
              : `${Math.floor(totalSecs / 60)}m${totalSecs % 60}s`;

          // 安全地读取 usage，并兜底为 0
          const inputTokens =
            (call.usage?.llm_prompt_tokens ?? 0) +
            (call.usage?.llm_prompt_cached_tokens ?? 0);
          const outputTokens = call.usage?.llm_completion_tokens ?? 0;

          return (
            <li
              key={call.job_id}
              onClick={() => onSelect(call)}
              className={`flex justify-between items-start p-3 rounded-lg cursor-pointer ${
                isSelected ? "bg-blue-50" : "hover:bg-gray-50"
              }`}
            >
              {/* left info */}
              <div className="flex items-start space-x-3">
                <Icon className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />
                <div>
                  <div className="font-medium text-gray-800">
                    {call.metadata.type} Call
                  </div>
                  <div className="text-xs text-gray-500">
                    {call.metadata.phone_number}
                  </div>
                  <div className="text-sm mt-1 text-gray-600">
                    {call.status === "successful"
                      ? "Call completed"
                      : call.status === "failed"
                      ? "Follow-up needed"
                      : call.status === "dispatched"
                      ? "In progress"
                      : call.status}
                  </div>
                </div>
              </div>

              {/* right info: two rows, right-aligned */}
              <div className="flex flex-col items-end text-xs text-gray-500">
                {/* first row: time & duration */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="w-4 h-4 text-gray-400" />
                    <span>{format(new Date(call.received_at), "HH:mm")}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock3 className="w-4 h-4 text-gray-400" />
                    <span>{durationText}</span>
                  </div>
                </div>
                {/* second row: input & output tokens */}
                <div className="flex items-center gap-4 mt-1">
                  <div className="flex items-center gap-1">
                    <Zap className="w-4 h-4 text-green-500" />
                    <span>{inputTokens}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <ZapOff className="w-4 h-4 text-yellow-500" />
                    <span>{outputTokens}</span>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div className="bg-white p-6 rounded-xl shadow-md overflow-y-auto max-h-[80vh]">
      {Object.entries(grouped).map(([date, list]) =>
        renderSection(date, list)
      )}
    </div>
  );
}
